import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { RecapEditor } from '../../components/crm/VisitAndBilling';
import { useAuth } from '../../context/AuthContext';
import { useSmartBack } from '../../hooks/useSmartBack';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

import { StatusTracker, STATUS_FLOW } from '../../components/admin/appointment/StatusTracker';
import {
  ClientInfoCard, ServiceInfoCard, TimeTrackingCard, PhotoGalleryCard, TimelineCard,
} from '../../components/admin/appointment/InfoCards';
import {
  CleanerAssignmentCard, BillingPanel, QCPanel, QuickInfoCard,
} from '../../components/admin/appointment/SidebarCards';
import MultiGuruSelect from '../../components/admin/MultiGuruSelect';
import { TimeCorrectionTools } from '../../components/admin/appointment/TimeCorrectionTools';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminAppointmentDetailPage = () => {
  const { id } = useParams();
  const { token } = useAuth();
  const navigate = useNavigate();
  const { back } = useSmartBack('/admin/appointments');
  const [appt, setAppt] = useState(null);
  const [timeEntries, setTimeEntries] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [invoice, setInvoice] = useState(null);
  const [cleaners, setCleaners] = useState([]);
  const [team, setTeam] = useState([]);           // ordered active guru IDs
  const [teamNames, setTeamNames] = useState([]); // matching names for supporting-guru display
  const [loading, setLoading] = useState(true);
  const [charging, setCharging] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);
  const [teamDraft, setTeamDraft] = useState([]);
  const [savingTeam, setSavingTeam] = useState(false);
  const [qcItems, setQCItems] = useState([]);
  const [qcPassing, setQCPassing] = useState(false);

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const fetchAll = useCallback(async () => {
    try {
      const [apptRes, photosRes, timelineRes, cleanersRes, teamRes] = await Promise.all([
        axios.get(`${API_URL}/api/appointments/${id}`, { headers }),
        axios.get(`${API_URL}/api/appointments/${id}/photos`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/appointments/${id}/timeline`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/cleaners`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/admin/assignments/appointment/${id}`, { headers }).catch(() => ({ data: { current_team: [] } })),
      ]);
      setAppt(apptRes.data);
      setPhotos(photosRes.data || []);
      setTimeline(timelineRes.data || []);
      setCleaners(cleanersRes.data || []);
      const activeTeam = (teamRes.data?.current_team || []);
      setTeam(activeTeam.map(t => t.staff_profile_id));
      setTeamNames(activeTeam.map(t => t.guru_name || 'Guru'));

      const teRes = await axios.get(`${API_URL}/api/time-entries?appointment_id=${id}`, { headers }).catch(() => ({ data: [] }));
      setTimeEntries(teRes.data || []);

      const qcRes = await axios.get(`${API_URL}/api/appointments/${id}/qc-items`, { headers }).catch(() => ({ data: [] }));
      setQCItems(qcRes.data || []);

      if (apptRes.data.invoice) {
        setInvoice(apptRes.data.invoice);
      } else {
        const invRes = await axios.get(`${API_URL}/api/invoices`, { headers }).catch(() => ({ data: [] }));
        const inv = (invRes.data || []).find(i => i.appointment_id === id);
        if (inv) setInvoice(inv);
      }
    } catch (_) {
      toast.error('Failed to load appointment');
    } finally {
      setLoading(false);
    }
  }, [id, headers]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleStatusChange = async (newStatus) => {
    try {
      await axios.put(`${API_URL}/api/appointments/${id}`, { status: newStatus }, { headers });
      toast.success(`Status updated to ${newStatus}`);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    }
  };

  const openAssignDialog = () => {
    setTeamDraft(team);
    setAssignOpen(true);
  };

  const handleSaveTeam = async () => {
    setSavingTeam(true);
    try {
      await axios.put(`${API_URL}/api/admin/assignments/appointment/${id}`,
        { staff_profile_ids: teamDraft }, { headers });
      toast.success(teamDraft.length === 0 ? 'Team cleared' : `Team updated (${teamDraft.length} guru${teamDraft.length !== 1 ? 's' : ''})`);
      setAssignOpen(false);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update team');
    } finally {
      setSavingTeam(false);
    }
  };

  const handleConfirmAndCharge = async () => {
    setCharging(true);
    try {
      await axios.post(`${API_URL}/api/payments/admin-confirm`, { appointment_id: id, tip_amount: 0 }, { headers });
      toast.success('Payment charged successfully!');
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Charge failed');
    } finally {
      setCharging(false);
    }
  };

  const handleQCPass = async () => {
    setQCPassing(true);
    try {
      await axios.post(`${API_URL}/api/appointments/${id}/qc-pass`, {}, { headers });
      toast.success('QC passed!');
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'QC pass failed');
    } finally {
      setQCPassing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50">
        <Loader2 className="h-8 w-8 animate-spin text-[#F56F05]" />
      </div>
    );
  }

  if (!appt) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-zinc-50">
        <p className="text-zinc-500 mb-4">Appointment not found</p>
        <Button onClick={back}>Back to Appointments</Button>
      </div>
    );
  }

  const currentIdx = STATUS_FLOW.indexOf(appt.status);
  const nextStatuses = STATUS_FLOW.filter((s, i) => i > currentIdx && i <= currentIdx + 1);
  const hasAfterPhoto = photos.some(p => p.photo_type === 'after');
  const hasClockOut = timeEntries.some(te => te.clock_out_at);
  const qcPassed = appt.qc_passed === true;
  const canCharge = appt.status === 'Service Finished' && hasAfterPhoto && hasClockOut && qcPassed;
  const checkedCount = qcItems.filter(i => i.is_checked).length;
  const requiredMissing = qcItems.filter(i => i.is_required && !i.is_checked);
  const qcProgress = qcItems.length > 0 ? Math.round((checkedCount / qcItems.length) * 100) : 0;
  const canQCPass = requiredMissing.length === 0 && qcItems.length > 0 && !qcPassed;

  const serviceCost = appt.created_via === 'admin_add_new_clean' && appt.quoted_price != null ? appt.quoted_price : (appt.service?.base_price || 0);
  const addOnsCost = (appt.add_ons || []).reduce((sum, a) => sum + (a.price || 0), 0);
  const subtotal = serviceCost + addOnsCost;
  const tax = 0;
  const total = subtotal + tax;

  return (
    <div className="min-h-screen bg-zinc-50">
      <header className="bg-white border-b border-zinc-200 sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="sm" onClick={back} data-testid="back-btn">
                <ArrowLeft className="h-4 w-4 mr-1" /> Appointments
              </Button>
              <span className="text-sm text-zinc-400">#{id.slice(0, 8)}</span>
            </div>
            <div className="flex items-center gap-2">
              {nextStatuses.map(s => (
                <Button key={s} size="sm" onClick={() => handleStatusChange(s)}
                  className="bg-[#F56F05] hover:bg-[#C2410C] text-white" data-testid={`set-status-${s.toLowerCase().replace(/ /g, '-')}`}>
                  {s}
                </Button>
              ))}
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <StatusTracker currentStatus={appt.status} />

        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <ClientInfoCard appt={appt} onOpenCustomer={(uid) => navigate(`/admin/customers/${uid}`)} />
            <ServiceInfoCard appt={appt} serviceCost={serviceCost} />
            <TimeTrackingCard timeEntries={timeEntries} />
            <TimeCorrectionTools appointmentId={id} timeEntries={timeEntries} headers={headers} onChanged={fetchAll}
              team={team.map((gid, i) => ({ id: gid, name: teamNames[i] }))} />
            <PhotoGalleryCard photos={photos} />
            <TimelineCard timeline={timeline} />
          </div>

          <div className="space-y-6">
            <CleanerAssignmentCard appt={appt} onOpenAssign={openAssignDialog}
              teamCount={team.length} teamNames={teamNames} />
            <BillingPanel
              serviceCost={serviceCost} addOnsCost={addOnsCost} tax={tax} total={total}
              invoice={invoice} canCharge={canCharge} hasClockOut={hasClockOut}
              hasAfterPhoto={hasAfterPhoto} qcPassed={qcPassed} appt={appt}
              charging={charging} onConfirmCharge={handleConfirmAndCharge}
            />
            <QCPanel
              qcItems={qcItems} qcPassed={qcPassed} qcProgress={qcProgress}
              checkedCount={checkedCount} requiredMissing={requiredMissing}
              canQCPass={canQCPass} qcPassing={qcPassing} onQCPass={handleQCPass}
            />
            <QuickInfoCard appt={appt} />
          </div>
        </div>
        <div className="mt-6"><RecapEditor appointmentId={id} headers={headers} /></div>
      </main>

      <Dialog open={assignOpen} onOpenChange={setAssignOpen}>
        <DialogContent className="sm:max-w-md" data-testid="assign-dialog">
          <DialogHeader>
            <DialogTitle>Assigned Team</DialogTitle>
            <DialogDescription>
              Pick every Guru working this job. The first Guru is the lead — they&apos;ll appear on the calendar and any legacy views.
            </DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <MultiGuruSelect
              value={teamDraft}
              onChange={setTeamDraft}
              headers={headers}
              gurusOverride={cleaners}
              testIdPrefix="detail-team"
              placeholder="Assign gurus…"
            />
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setAssignOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveTeam} disabled={savingTeam}
              className="bg-[#F56F05] hover:bg-[#C2410C] text-white" data-testid="confirm-assign-btn">
              {savingTeam ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
              Save team
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminAppointmentDetailPage;
